import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Icon from "../components/Icon";
import { Button, Field } from "../components/ui";
import PhotoButton from "../components/PhotoButton";
import SignOff from "../components/SignOff";
import VoiceNote from "../components/VoiceNote";
import { useAuth } from "../auth";
import { apiGet, apiSend } from "../api";
import { getDeviceId } from "../deviceId";
import { getSticky, setSticky } from "../sticky";
import { C, R, S } from "../theme";

const label = { color: C.sub, fontSize: 13, fontWeight: 700, marginBottom: S.sm, textTransform: "uppercase" };
const ta = { background: C.card2, borderRadius: R.md, border: `1px solid ${C.borderStrong}`, color: C.text, fontSize: 16, padding: S.md, minHeight: 90, width: "100%", outline: "none", marginBottom: S.sm };

export default function IncidentNew() {
  const { aid } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [attraction, setAttraction] = useState(null);
  const [occurredAt, setOccurredAt] = useState(new Date().toISOString().slice(0, 16));
  const [injured, setInjured] = useState("");
  const [injuredRole, setInjuredRole] = useState("");
  const [contact, setContact] = useState("");
  const [desc, setDesc] = useState("");
  const [photos, setPhotos] = useState([]);
  const [staffOnDuty, setStaff] = useState("");
  const [witnesses, setWitnesses] = useState("");
  const [witnessContact, setWitnessContact] = useState("");
  const [weather, setWeather] = useState("");
  const [firstAid, setFirstAid] = useState("");
  const [loc, setLoc] = useState(getSticky("location"));
  const [signoff, setSignoff] = useState({ signature: null, name: user?.display_name || user?.company_name || "", initials: "", complete: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { apiGet(`/attractions/${aid}`).then(setAttraction).catch(() => {}); }, [aid]);

  const save = async () => {
    setErr("");
    if (!injured.trim() || !desc.trim()) { setErr("Injured person name and description are required"); return; }
    if (!signoff.complete) { setErr("Signature, printed name and initials are all required"); return; }
    setBusy(true);
    try {
      if (loc) setSticky("location", loc);
      await apiSend(`/attractions/${aid}/incidents`, "POST", {
        occurred_at: new Date(occurredAt).toISOString(), injured_name: injured, injured_contact: contact || null,
        injured_role: injuredRole || null, description: desc, photos, staff_on_duty: staffOnDuty || null,
        witnesses: witnesses || null, witness_contact: witnessContact || null, weather_conditions: weather || null,
        first_aid: firstAid || null, location: loc || null,
        signature: signoff.signature, signed_by_name: signoff.name, signed_by_initials: signoff.initials, device_id: getDeviceId(),
      });
      nav(-1);
    } catch (e) { setErr(e?.message || "Save failed"); } finally { setBusy(false); }
  };

  const roleBtn = (key, txt) => (
    <button data-testid={`inc-role-${key || "none"}`} onClick={() => setInjuredRole(key)} style={{ flex: 1, background: injuredRole === key ? C.amber : C.card2, borderRadius: R.md, padding: `${S.md}px 0`, color: injuredRole === key ? C.black : C.text, fontWeight: 800, border: `1px solid ${C.borderStrong}`, textTransform: "capitalize" }}>{txt}</button>
  );

  return (
    <div className="fade-up">
      <div style={{ display: "flex", alignItems: "center", gap: S.sm, padding: S.lg }}>
        <button className="pressable" onClick={() => nav(-1)}><Icon name="chevron-back" size={28} color={C.text} /></button>
        <div className="display" style={{ color: C.text, fontSize: 20, fontWeight: 900 }}>Log Incident</div>
      </div>
      <div style={{ padding: S.lg }}>
        {attraction && <div style={{ color: C.muted, marginBottom: S.md }}>{attraction.name}</div>}
        <Field label="Date & time" testID="inc-when" type="datetime-local" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} />
        <Field label="Injured person name" testID="inc-injured" value={injured} onChange={(e) => setInjured(e.target.value)} />
        <div style={label}>Injured person is (optional)</div>
        <div style={{ display: "flex", gap: S.sm, marginBottom: S.md }}>
          {roleBtn("public", "Member of public")}
          {roleBtn("staff", "Staff member")}
        </div>
        <Field label="Injured person contact (optional)" testID="inc-contact" value={contact} onChange={(e) => setContact(e.target.value)} />
        <div style={label}>Description *</div>
        <textarea data-testid="inc-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What happened?" style={ta} />
        <VoiceNote onText={(t) => setDesc((d) => (d + " " + t).trim())} />
        <div style={{ height: S.md }} />
        <Field label="Weather / ground conditions (optional)" testID="inc-weather" value={weather} onChange={(e) => setWeather(e.target.value)} placeholder="e.g. Dry, light wind ~10 mph" />
        <div style={label}>First aid administered (optional)</div>
        <textarea data-testid="inc-firstaid" value={firstAid} onChange={(e) => setFirstAid(e.target.value)} placeholder="First aid given, by whom, ambulance called?" style={{ ...ta, minHeight: 70 }} />
        <div style={label}>Scene photos</div>
        {photos.map((p, i) => <img key={i} src={p} alt="" style={{ width: "100%", borderRadius: R.md, marginBottom: S.sm }} />)}
        <PhotoButton onCapture={(p) => setPhotos((ps) => [...ps, p])} allowGallery testID="inc-photo" />
        <div style={{ height: S.md }} />
        <Field label="Staff on duty (optional)" testID="inc-staff" value={staffOnDuty} onChange={(e) => setStaff(e.target.value)} />
        <Field label="Witnesses (optional)" testID="inc-witnesses" value={witnesses} onChange={(e) => setWitnesses(e.target.value)} />
        <Field label="Witness contact details (optional)" testID="inc-witness-contact" value={witnessContact} onChange={(e) => setWitnessContact(e.target.value)} />
        <Field label="Location / site (optional)" testID="inc-location" value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="e.g. North field, Pitch 3" />
        <SignOff defaultName={signoff.name} onChange={setSignoff} />
        <div style={{ height: S.md }} />
        {!!err && <div style={{ color: C.red, fontWeight: 700, marginBottom: S.sm }}>{err}</div>}
        <Button title="Save Immutable Incident Record" testID="inc-save" variant="red" onPress={save} loading={busy} />
        <div style={{ height: S.xxl }} />
      </div>
    </div>
  );
}
